// Optimized Chatbot component with memoization and stable callbacks
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { IoMdSend } from "react-icons/io";
import { useTranslation } from "react-i18next";
import { ChatState, initialState, AiProfile } from "./chatState";
import { paths } from "./paths";
import TypingIndicator from "./TypingIndicator";
import {
  getCurrentUser,
  syncUserWithServer,
  updateUser,
  getCurrentOrganization,
} from "src/features/profile/state/profileSlice";
import { useAppDispatch } from "src/common/state/hooks";
import {
  resetActiveGptConversation,
  createChat,
  updateChat,
  askWixarpedia,
  setActiveGptConversation,
  fetchGptConversations,
  setChosenLlmProvider,
  getChosenLlmProvider,
  getScenarioHelperRagAgentIds,
  setScenarioHelperRagAgentIds,
  resetScenarioHelperRagAgentIds,
} from "../state/chatbotSlice";
import ChatBubble from "./ChatBubble";
import GraphBubble from "./GraphBubble";
import { IGptConversation } from "src/model/model";
import { useSelector, shallowEqual } from "react-redux";
import { GPT_CONVERSATION_LOADER_COMPONENT_ID } from "./GptConversationLoader";
import ModalStandard from "src/common/components/Modal/ModalStandard";
import CreateProjectFromAiGraph from "./CreateProjectFromAiGraph";
import { closeStandardModal, openStandardModal } from "src/common/state/slice/modal/modalSlice";
import { IsLoading } from "src/common/components/AppState/IsLoading";
import GeneratePdfFromScenarioHelperResponse from "./GeneratePdfFromScenarioHelperResponse";
import { generateHashFromString } from "../utils/generateHashFromString";
import { isMarkdownShapedLikeAScenario } from "../utils/isMarkdownShapedLikeAScenario";
import LlmProviderDropdown from "./LlmProviderDropdown";
import { Link } from "react-router-dom";
import "./Chatbot.scss";
import getUserPreferredLlmProvider from "../utils/getUserPrefferedLlmProvider";
import { useDictation } from "src/features/tts-and-stt/hooks/useDictation";
import { useConversationMode } from "src/features/tts-and-stt/hooks/useConversationMode";
import MicrophoneButton from "src/features/tts-and-stt/components/MicrophoneButton";
import VoiceModeToggle from "src/features/tts-and-stt/components/VoiceModeToggle";
import SpeakingIndicator from "src/features/tts-and-stt/components/SpeakingIndicator";

interface Message {
  text: string;
  sender: "bot" | "user";
}

interface ChatbotProps {
  mode: "scenarioHelper" | "wixarpedia";
  activeConversation?: IGptConversation | null;
  onGenerateProject?: () => void;
}

export const CHATBOT_COMPONENT_ID = "chatbotComponentId";
export const createProjectFromAiGraphModalId = "createProjectFromAiGraphModalId";

const Chatbot: React.FC<ChatbotProps> = ({ mode, activeConversation, onGenerateProject }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const loadInitialMessages = useCallback((): Message[] => {
    if (mode === "wixarpedia") {
      const saved = localStorage.getItem("wixarpediaChatMessages");
      return saved
        ? JSON.parse(saved)
        : [{ text: "Bonjour ! Comment puis-je vous aider ?", sender: "bot" }];
    }
    return [];
  }, [mode]);

  const [chatState, setChatState] = useState<ChatState>(initialState);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messages, setMessages] = useState<Message[]>(loadInitialMessages);
  const [userInput, setUserInput] = useState("");
  const [inputMode, setInputMode] = useState(true);
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState<"metier" | "targetAudience">("metier");
  const [onboardingMetier, setOnboardingMetier] = useState("");
  const [onboardingCustomMetier, setOnboardingCustomMetier] = useState("");
  const [onboardingShowCustomMetier, setOnboardingShowCustomMetier] = useState(false);
  const [onboardingTargetAudience, setOnboardingTargetAudience] = useState("");
  const [sessionConversationId, setSessionConversationId] = useState<number | null>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  const user = useSelector(getCurrentUser, shallowEqual);
  const organization = useSelector(getCurrentOrganization);
  const chosenLlmProvider = useSelector(getChosenLlmProvider);
  const ragAgentIds = useSelector(getScenarioHelperRagAgentIds, shallowEqual);

  const isSubmitDisabled = useMemo(
    () => !userInput.trim() || isSubmitting,
    [userInput, isSubmitting],
  );

  // Refs bridge the cyclic dep between conversation (needs to call dictation.startListening)
  // and dictation (needs to call submit-with-text in auto-send path).
  const dictationRef = useRef<ReturnType<typeof useDictation> | null>(null);
  const submitWithTextRef = useRef<((text: string) => Promise<void>) | null>(null);

  const conversation = useConversationMode({
    onAutoListenCue: () => {
      dictationRef.current?.startListening().catch(() => undefined);
    },
    onDeactivate: () => {
      dictationRef.current?.stopListening().catch(() => undefined);
    },
  });

  const dictation = useDictation({
    // Conversation mode wants a short silence timeout so the user's reply auto-sends;
    // dictation mode wants a long one so thinking pauses don't cut the user off.
    silenceTimeout: conversation.voiceModeEnabled ? 4000 : 30000,
    onTranscript: (text, { viaTimeout }) => {
      if (conversation.voiceModeEnabled && viaTimeout && text.trim()) {
        submitWithTextRef.current?.(text).catch(() => undefined);
      } else {
        setUserInput(text);
      }
    },
  });
  dictationRef.current = dictation;

  // Mirror live transcript into userInput while listening so the textarea
  // has a single source of truth and never shows stale state.
  useEffect(() => {
    if (!dictation.isListening) return;
    const live = [dictation.transcript, dictation.interimTranscript].filter(Boolean).join(" ");
    setUserInput(live);
  }, [dictation.isListening, dictation.transcript, dictation.interimTranscript]);

  // Track whether a TTS streaming session is open for the current bot reply.
  const ttsSessionOpenRef = useRef(false);

  const getSavedAiProfile = useCallback((): AiProfile | null => {
    try {
      const prefs = (user as any)?.preferences;
      if (!prefs) return null;
      const obj = typeof prefs === "string" ? JSON.parse(prefs) : prefs;
      const profile = obj?.aiProfile;
      if (profile?.metier) return profile;
      return null;
    } catch {
      return null;
    }
  }, [user]);

  const orgCtx = useMemo(() => {
    const ctx = (organization as any)?.ai_context;
    if (!ctx || typeof ctx !== "object") return null;
    return ctx.sector || ctx.description ? ctx : null;
  }, [organization]);

  const hasUserProfile = useMemo(() => !!getSavedAiProfile()?.metier, [getSavedAiProfile]);

  const aiContextParts = useMemo(() => {
    const parts: string[] = [];
    const profile = getSavedAiProfile();
    if (profile?.metier) parts.push(profile.metier);
    if (profile?.targetAudience) parts.push(profile.targetAudience);
    if (orgCtx?.sector) parts.push(orgCtx.sector);
    if (orgCtx?.description) parts.push(orgCtx.description);
    return parts;
  }, [getSavedAiProfile, orgCtx]);

  // Persist messages for wixarpedia
  useEffect(() => {
    if (mode === "wixarpedia") {
      localStorage.setItem("wixarpediaChatMessages", JSON.stringify(messages));
    }
  }, [messages, mode]);

  // Initialize conversation on mode or activeConversation change
  useEffect(() => {
    if (mode === "scenarioHelper") {
      activeConversation ? loadPreviousConversation() : resetChatLocally();
    } else {
      resetChatLocally();
    }
  }, [mode, activeConversation]);

  // Scroll chat into view
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  // Set active GPT conversation on user load
  useEffect(() => {
    if (user?.active_gpt_conversation?.id) {
      dispatch(setActiveGptConversation(user.active_gpt_conversation));
    }
  }, [user?.active_gpt_conversation?.id, dispatch]);

  // Set initial LLM provider
  useEffect(() => {
    const initial = getUserPreferredLlmProvider(user?.preferences);
    dispatch(setChosenLlmProvider(initial));
  }, [user?.preferences, dispatch]);

  // Ensure modal closed on mount
  useEffect(() => {
    dispatch(closeStandardModal(createProjectFromAiGraphModalId));
  }, [dispatch]);

  const loadPreviousConversation = useCallback(() => {
    if (!activeConversation) return;
    setShowOnboardingModal(false);
    const { conversation_metadata, conversation_data } = activeConversation;
    setChatState((prev) => ({ ...prev, userFormResults: conversation_metadata.userFormResults }));
    const msgs: Message[] = [];
    const { userPrompts } = conversation_metadata;
    const { output } = conversation_data;
    const total = Math.max(userPrompts.length, output.length);
    for (let i = 0; i < total; i++) {
      if (i < userPrompts.length) msgs.push({ text: userPrompts[i], sender: "user" });
      if (i < output.length) msgs.push({ text: output[i], sender: "bot" });
    }
    setMessages(msgs);
    setInputMode(true);

    // Restore RAG agent IDs from conversation metadata, or clear selection
    const storedRagAgentIds = activeConversation.conversation_metadata?.rag_agent_ids;
    if (Array.isArray(storedRagAgentIds) && storedRagAgentIds.length > 0) {
      dispatch(setScenarioHelperRagAgentIds(storedRagAgentIds));
    } else {
      dispatch(resetScenarioHelperRagAgentIds());
    }
  }, [activeConversation, dispatch]);

  const resetChatLocally = useCallback(() => {
    setChatState(initialState);
    setIsBotTyping(false);
    setUserInput("");
    setSessionConversationId(null);

    if (mode === "wixarpedia") {
      localStorage.removeItem("wixarpediaChatMessages");
      setMessages([{ text: "Bonjour ! Comment puis-je vous aider aujourd'hui ?", sender: "bot" }]);
      return;
    }

    // scenarioHelper mode
    dispatch(resetActiveGptConversation());
    dispatch(resetScenarioHelperRagAgentIds());

    setMessages([{ text: "Bonjour. Quelle est votre problématique ?", sender: "bot" }]);
    setInputMode(true);

    const savedProfile = getSavedAiProfile();
    if (savedProfile?.metier) {
      // Profile already saved — inject into state silently
      setChatState((prev) => ({
        ...prev,
        userFormResults: {
          metier: savedProfile.metier,
          targetAudience: savedProfile.targetAudience,
        },
      }));
    } else {
      // No saved profile — show onboarding modal
      setOnboardingStep("metier");
      setOnboardingMetier("");
      setOnboardingTargetAudience("");
      setShowOnboardingModal(true);
    }
  }, [mode, dispatch, getSavedAiProfile]);

  const updateBotMessage = useCallback(
    (chunk: string) => {
      setMessages((prev) => {
        const msgs = [...prev];
        const i = msgs.length - 1;
        msgs[i] = { ...msgs[i], text: msgs[i].text + chunk };
        return msgs;
      });
      if (ttsSessionOpenRef.current) {
        conversation.pushSpeakingChunk(chunk);
      }
    },
    [conversation],
  );

  const saveAiProfileToPreferences = useCallback(
    (formResults: { metier?: string; targetAudience?: string }) => {
      if (!user?.id || !formResults.metier) return;
      try {
        const prefs = (user as any)?.preferences;
        const base =
          typeof prefs === "string"
            ? JSON.parse(prefs) ?? {}
            : typeof prefs === "object" && prefs !== null
            ? { ...prefs }
            : {};
        base.aiProfile = {
          metier: formResults.metier,
          targetAudience: formResults.targetAudience,
        };
        dispatch(
          updateUser({
            id: Number(user.id),
            preferences: base,
          } as any),
        );
      } catch (e) {
        console.error("Failed to save AI profile:", e);
      }
    },
    [user, dispatch],
  );

  const handleOnboardingMetierSelect = useCallback((value: string) => {
    if (value === "Autre") {
      setOnboardingShowCustomMetier(true);
      setOnboardingCustomMetier("");
    } else {
      setOnboardingMetier(value);
      setOnboardingShowCustomMetier(false);
      setOnboardingStep("targetAudience");
    }
  }, []);

  const handleCustomMetierSubmit = useCallback(() => {
    const value = onboardingCustomMetier.trim();
    if (!value) return;
    setOnboardingMetier(value);
    setOnboardingShowCustomMetier(false);
    setOnboardingStep("targetAudience");
  }, [onboardingCustomMetier]);

  const handleOnboardingSubmit = useCallback(() => {
    const formResults = {
      metier: onboardingMetier,
      targetAudience: onboardingTargetAudience.trim() || undefined,
    };
    setChatState((prev) => ({
      ...prev,
      userFormResults: formResults,
    }));
    setShowOnboardingModal(false);
    saveAiProfileToPreferences(formResults);
  }, [onboardingMetier, onboardingTargetAudience, saveAiProfileToPreferences]);

  const handleOnboardingSkip = useCallback(() => {
    setShowOnboardingModal(false);
  }, []);

  const handleUserPromptSubmitWithText = useCallback(async (text: string) => {
    if (!text.trim()) return;
    const prompt = text;
    setUserInput("");
    setIsSubmitting(true);
    setMessages((prev) => [...prev, { text: prompt, sender: "user" }, { text: "", sender: "bot" }]);
    setIsBotTyping(true);

    // Open the TTS streaming session BEFORE the LLM call so the WS handshake
    // overlaps with the bot's first tokens. Chunks are pushed live via
    // updateBotMessage; the session is closed in the finally block.
    if (conversation.voiceModeEnabled) {
      ttsSessionOpenRef.current = true;
      conversation.beginSpeakingSession().catch((err) => {
        console.error("Voice mode session error:", err);
        ttsSessionOpenRef.current = false;
      });
    }

    try {
      if (mode === "scenarioHelper") {
        const conversationId = activeConversation?.id ?? sessionConversationId;
        if (conversationId) {
          await dispatch(
            updateChat({
              userPrompt: prompt,
              conversationId,
              ragAgentIds,
              cb: updateBotMessage,
            }),
          );
          dispatch(syncUserWithServer({}));
        } else {
          const result = await dispatch(
            createChat({
              userPrompt: prompt,
              userFormResults: chatState.userFormResults,
              sourceMediasWithContext: [],
              chosenLlmProvider,
              ragAgentIds,
              sourceMedias: [],
              cb: updateBotMessage,
              cbOnRequestEnd: () => {
                dispatch(
                  fetchGptConversations({
                    componentId: GPT_CONVERSATION_LOADER_COMPONENT_ID,
                    start: 0,
                  }),
                );
                dispatch(syncUserWithServer({}));
              },
            }),
          );
          const created = result.payload as IGptConversation | undefined;
          if (created?.id) {
            setSessionConversationId(created.id);
          }
        }
      } else {
        await dispatch(askWixarpedia({ userPrompt: prompt, cb: updateBotMessage }));
      }
    } finally {
      if (ttsSessionOpenRef.current) {
        conversation.endSpeakingSession();
        ttsSessionOpenRef.current = false;
      }
      setIsSubmitting(false);
      setIsBotTyping(false);
    }
  }, [
    activeConversation,
    sessionConversationId,
    chatState.userFormResults,
    dispatch,
    mode,
    chosenLlmProvider,
    ragAgentIds,
    updateBotMessage,
    conversation,
  ]);

  const handleUserPromptSubmit = useCallback(
    () => handleUserPromptSubmitWithText(userInput),
    [userInput, handleUserPromptSubmitWithText],
  );

  // Wire the auto-send path: dictation's onTranscript (closing over an outdated
  // submit) reads the current submit fn through this ref each time it fires.
  submitWithTextRef.current = handleUserPromptSubmitWithText;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (userInput.trim() && !isSubmitting) handleUserPromptSubmit();
      }
    },
    [userInput, isSubmitting, handleUserPromptSubmit],
  );

  const renderedMessages = useMemo(
    () =>
      messages.map((message, idx) => (
        <div key={idx}>
          <ChatBubble customKey={idx} sender={message.sender} text={message.text} />
          {isMarkdownShapedLikeAScenario(message.text) && (
            <>
              <GraphBubble
                markdown={message.text}
                mdStringId={generateHashFromString(message.text)}
              />
              {!isSubmitting && (
                <div className="flex w-full justify-center">
                  {message.text && (
                    <GeneratePdfFromScenarioHelperResponse
                      markdown={message.text}
                      key={`${message.text}-${idx}`}
                    />
                  )}
                  <button
                    className="btn-primary-outline mt-1 w-3/5"
                    onClick={() =>
                      onGenerateProject
                        ? onGenerateProject()
                        : dispatch(openStandardModal(createProjectFromAiGraphModalId))
                    }
                  >
                    {t("pages.chat.generateModuleFromGraph")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )),
    [messages, isSubmitting, dispatch, t],
  );

  return (
    <div className="flex h-full">
      <div className="flex m-2 w-full flex-col items-center">
        <div className="w-full h-full p-4 bg-white shadow-lg rounded-lg flex flex-col relative">
          {showOnboardingModal && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80 rounded-lg backdrop-blur-sm">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 w-[460px] max-w-[90%] p-6">
                <h3 className="text-lg font-medium mb-1">{t("pages.chat.onboarding.title")}</h3>
                <p className="text-sm text-gray-500 mb-5">{t("pages.chat.onboarding.subtitle")}</p>

                {onboardingStep === "metier" && (
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      {t("pages.chat.onboarding.metierQuestion")}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(paths.metier.options || []).map((opt) => (
                        <button
                          key={opt.value}
                          className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
                            opt.value === "Autre" && onboardingShowCustomMetier
                              ? "border-green-500 bg-green-50"
                              : "border-gray-300 hover:bg-green-50 hover:border-green-500"
                          }`}
                          onClick={() => handleOnboardingMetierSelect(opt.value)}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    {onboardingShowCustomMetier && (
                      <div className="flex gap-2 mt-3">
                        <input
                          type="text"
                          value={onboardingCustomMetier}
                          onChange={(e) => setOnboardingCustomMetier(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && onboardingCustomMetier.trim()) {
                              e.preventDefault();
                              handleCustomMetierSubmit();
                            }
                          }}
                          placeholder={t("pages.chat.onboarding.customMetierPlaceholder")}
                          className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                          autoFocus
                        />
                        <button
                          className="btn-primary-fill text-sm"
                          onClick={handleCustomMetierSubmit}
                          disabled={!onboardingCustomMetier.trim()}
                        >
                          OK
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {onboardingStep === "targetAudience" && (
                  <div>
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                      {onboardingMetier}
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-3">
                      {t("pages.chat.onboarding.targetAudienceQuestion")}
                    </p>
                    <input
                      type="text"
                      value={onboardingTargetAudience}
                      onChange={(e) => setOnboardingTargetAudience(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleOnboardingSubmit();
                        }
                      }}
                      placeholder={t("pages.chat.onboarding.targetAudiencePlaceholder")}
                      className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:border-green-500 focus:ring-1 focus:ring-green-500"
                      autoFocus
                    />
                  </div>
                )}

                <div className="flex items-center justify-between mt-5 pt-4 border-t border-gray-100">
                  {onboardingStep === "metier" ? (
                    <>
                      <button
                        className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={handleOnboardingSkip}
                      >
                        {t("pages.chat.onboarding.skip")}
                      </button>
                      <div />
                    </>
                  ) : (
                    <>
                      <button
                        className="btn-alternative-outline text-sm"
                        onClick={() => setOnboardingStep("metier")}
                      >
                        {t("general.back")}
                      </button>
                      <button className="btn-primary-fill" onClick={handleOnboardingSubmit}>
                        {t("pages.chat.onboarding.start")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {mode === "scenarioHelper" &&
            (aiContextParts.length > 0 || !orgCtx || !hasUserProfile) && (
              <div className="flex items-center justify-between px-3 py-1.5 mb-2 bg-gray-50 rounded-lg border border-gray-200 text-xs text-gray-500">
                <span className="truncate">
                  {aiContextParts.length > 0 && (
                    <>
                      {t("pages.chat.aiContext")} {aiContextParts.join(" · ")}
                      {(!orgCtx || !hasUserProfile) && (
                        <span className="ml-2 text-gray-400">—</span>
                      )}
                    </>
                  )}
                  {!hasUserProfile && !orgCtx && (
                    <span className="text-amber-500">
                      {t("pages.chat.aiContextHintBoth")}{" "}
                      <Link to="/profile/me" className="underline hover:text-amber-600">
                        {t("pages.chat.aiContextHintProfile")}
                      </Link>{" "}
                      {t("pages.chat.aiContextHintAnd")}{" "}
                      <Link to="/profile/organization" className="underline hover:text-amber-600">
                        {t("pages.chat.aiContextHintOrg")}
                      </Link>{" "}
                      {t("pages.chat.aiContextHintSuffix")}
                    </span>
                  )}
                  {hasUserProfile && !orgCtx && (
                    <Link
                      to="/profile/organization"
                      className="text-amber-500 hover:text-amber-600"
                    >
                      {t("pages.chat.aiContextOrgHint")}
                    </Link>
                  )}
                  {!hasUserProfile && orgCtx && (
                    <Link to="/profile/me" className="text-amber-500 hover:text-amber-600">
                      {t("pages.chat.aiContextProfileHint")}
                    </Link>
                  )}
                </span>
                {(hasUserProfile || orgCtx) && (
                  <Link
                    to="/profile/me"
                    className="ml-3 shrink-0 text-purple-500 hover:text-purple-600 font-medium"
                  >
                    {t("general.edit")}
                  </Link>
                )}
              </div>
            )}
          <div className="chat-window bg-gray-100 p-4 flex-grow overflow-y-scroll rounded-md flex flex-col space-y-4">
            {renderedMessages}
            {isBotTyping && (
              <div className="flex justify-start">
                <div className="px-4 py-3 rounded-xl rounded-bl-sm bg-gray-50 border border-gray-200 text-gray-500">
                  <TypingIndicator />
                </div>
              </div>
            )}
            {mode === "scenarioHelper" &&
              !activeConversation &&
              messages.length <= 1 &&
              !isBotTyping && (
                <div className="flex-1 flex items-center justify-center">
                  <p className="text-sm text-gray-400 text-center max-w-md">
                    {t("pages.chat.scenarioHelperEmptyHint")}
                  </p>
                </div>
              )}
            {/* Inline form buttons removed — onboarding modal used instead */}
            <div ref={bottomRef} />
          </div>
          {inputMode && (
            <div className="flex-shrink-0 border-t border-gray-200 px-4 pt-3 pb-2 bg-white">
              <div className="flex items-center justify-between mb-1.5 min-h-[28px]">
                <SpeakingIndicator
                  isSpeaking={conversation.isSpeaking}
                  onStop={conversation.stopSpeaking}
                />
                <div className="ml-auto mr-20">
                  <VoiceModeToggle
                    enabled={conversation.voiceModeEnabled}
                    onToggle={conversation.toggleVoiceMode}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              <div className="flex gap-2 items-end">
                <textarea
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={1}
                  readOnly={dictation.isListening}
                  className={`flex-1 resize-none rounded-xl border px-4 py-2.5 text-sm focus:outline-none focus:ring-1 disabled:bg-gray-100 disabled:text-gray-400 transition-all overflow-y-auto max-h-40 ${
                    dictation.isListening
                      ? "border-red-500 bg-red-50 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 focus:border-green-500 focus:ring-green-500"
                  }`}
                  placeholder={
                    mode === "scenarioHelper"
                      ? t("pages.chat.scenarioHelperInputPlaceholder")
                      : t("pages.chat.wixarpediaInputPlaceholder")
                  }
                  disabled={isSubmitting}
                />
                <MicrophoneButton
                  isListening={dictation.isListening}
                  isSupported={dictation.isSupported}
                  disabled={isSubmitting}
                  idleTooltipKey={
                    conversation.voiceModeEnabled
                      ? "voice.conversationStartTooltip"
                      : "voice.dictateTooltip"
                  }
                  listeningTooltipKey={
                    conversation.voiceModeEnabled
                      ? "voice.conversationListeningTooltip"
                      : "voice.dictateListeningTooltip"
                  }
                  cooldown={
                    conversation.voiceModeEnabled
                      ? { triggerAt: dictation.lastActivityAt, durationMs: 4000 }
                      : undefined
                  }
                  onStartListening={() => {
                    // Barge-in: if the bot is speaking and conversation mode is on,
                    // stop TTS so the mic can take over cleanly.
                    if (conversation.voiceModeEnabled && conversation.isSpeaking) {
                      conversation.stopSpeaking();
                    }
                    dictation.resetTranscript();
                    dictation.startListening().catch((err) =>
                      console.error("STT start error:", err),
                    );
                  }}
                  onStopListening={() => {
                    // Re-click while listening = stop only, no send (both modes).
                    // Disarm the conversation auto-listen cue so a stale flag
                    // doesn't re-arm the mic later.
                    conversation.disarmAutoListen();
                    dictation.stopListening().catch(() => undefined);
                  }}
                />
                <button
                  onClick={handleUserPromptSubmit}
                  disabled={isSubmitDisabled}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-green-600 rounded-xl transition-colors"
                >
                  <IoMdSend size={16} />
                </button>
              </div>
              <div className="flex justify-between items-center mt-1.5">
                {mode === "scenarioHelper" && (
                  <LlmProviderDropdown
                    defaultValue={chosenLlmProvider}
                    onChange={(value) => dispatch(setChosenLlmProvider(value))}
                    disabled={Boolean(activeConversation) || isSubmitting}
                  />
                )}
                <p className="text-xs text-gray-400 ml-auto">
                  Shift+Entrée pour un retour à la ligne
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {!onGenerateProject && (
        <ModalStandard
          id={createProjectFromAiGraphModalId}
          isStatic
          modalType="standard"
          className="w-[60%] h-auto"
          showCloseButton
        >
          <CreateProjectFromAiGraph />
        </ModalStandard>
      )}
      <IsLoading componentId={CHATBOT_COMPONENT_ID} showSuccess={false} />

      {/* Onboarding modal removed — inline overlay used instead */}
    </div>
  );
};

export default React.memo(Chatbot);
